import {
  CertificateElement,
  CertificatePageSettings,
  CertificateTemplate,
} from "./certificateTypes";

export const DEFAULT_CERTIFICATE_LOGO =
  "data:image/svg+xml;charset=UTF-8," +
  encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80" fill="none">
  <rect width="80" height="80" rx="16" fill="#EEF2FF"/>
  <path d="M40 14c-7 8-16 12-16 26 0 10 7 18 16 26 9-8 16-16 16-26 0-14-9-18-16-26z" fill="#4F46E5"/>
  <path d="M40 22c-4 6-9 9-9 18 0 7 4 12 9 18 5-6 9-11 9-18 0-9-5-12-9-18z" fill="#FFFFFF" opacity=".9"/>
</svg>`);

const PAGE: CertificatePageSettings = {
  size: "A4",
  orientation: "landscape",
  marginMm: 18,
  fontFamily: "Georgia, 'Times New Roman', serif",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: 1.45,
  textAlign: "center",
  logoSrc: DEFAULT_CERTIFICATE_LOGO,
  logoSizeMm: 22,
  fieldSpacing: 3,
  paragraphSpacing: 4,
  labelValueStyle: "inline",
  showSafeArea: true,
  headerAlign: "center",
  footerText: "Thank You!",
};

const text = (
  partial: Omit<CertificateElement, "type" | "visible" | "lineHeight"> & {
    visible?: boolean;
    lineHeight?: number;
  }
): CertificateElement => ({
  type: "text",
  visible: true,
  lineHeight: 1.4,
  ...partial,
});

function standardElements(body: {
  title: string;
  certifyLine: string;
}): CertificateElement[] {
  const full = { x: 24, width: 249 };
  return [
    {
      id: "logo",
      type: "logo",
      label: "Clinic logo",
      section: "header",
      x: 124,
      y: 10,
      width: 50,
      height: 16,
      content: "",
      visible: true,
      fontSize: 12,
      fontWeight: 400,
      align: "center",
      lineHeight: 1,
    },
    text({
      id: "title",
      label: "Certificate title",
      section: "body",
      ...full,
      y: 30,
      height: 8,
      content: body.title,
      fontSize: 14,
      fontWeight: 700,
      align: "center",
      letterSpacing: 1.6,
      textTransform: "uppercase",
      color: "#0f172a",
    }),
    text({
      id: "clinicName",
      label: "Clinic name",
      section: "header",
      ...full,
      y: 40,
      height: 9,
      content: "{{clinicName}}",
      fontSize: 16,
      fontWeight: 700,
      align: "center",
      letterSpacing: 0.4,
      textTransform: "uppercase",
      color: "#0f172a",
      fieldKey: "clinicName",
    }),
    text({
      id: "clinicAddress",
      label: "Clinic address",
      section: "header",
      ...full,
      y: 50,
      height: 6,
      content: "{{clinicAddress}}",
      fontSize: 10,
      fontWeight: 400,
      align: "center",
      color: "#334155",
      fieldKey: "clinicAddress",
    }),
    text({
      id: "clinicContact",
      label: "Contact information",
      section: "header",
      ...full,
      y: 56,
      height: 5,
      content: "Tel: {{clinicPhone}}  |  WhatsApp : {{clinicWhatsapp}}",
      fontSize: 9,
      fontWeight: 400,
      align: "center",
      color: "#475569",
    }),
    text({
      id: "clinicWeb",
      label: "Website / Email",
      section: "header",
      ...full,
      y: 62,
      height: 5,
      content: "Web: {{clinicWebsite}}  |  Email: {{clinicEmail}}",
      fontSize: 9,
      fontWeight: 400,
      align: "center",
      color: "#475569",
    }),
    text({
      id: "doctorName",
      label: "Doctor name",
      section: "footer",
      x: 24,
      y: 78,
      width: 150,
      height: 6,
      content: "{{doctorName}}",
      fontSize: 11,
      fontWeight: 700,
      align: "left",
      color: "#0f172a",
      fieldKey: "doctorName",
    }),
    text({
      id: "qualifications",
      label: "Qualifications",
      section: "footer",
      x: 24,
      y: 84,
      width: 160,
      height: 5,
      content: "{{doctorQualifications}}",
      fontSize: 9,
      fontWeight: 400,
      align: "left",
      color: "#334155",
      fieldKey: "doctorQualifications",
    }),
    text({
      id: "designation",
      label: "Designation",
      section: "footer",
      x: 24,
      y: 90,
      width: 160,
      height: 5,
      content: "{{doctorDesignation}}",
      fontSize: 9,
      fontWeight: 400,
      align: "left",
      color: "#334155",
      fieldKey: "doctorDesignation",
    }),
    text({
      id: "slmcNumber",
      label: "SLMC registration number",
      section: "footer",
      x: 24,
      y: 96,
      width: 160,
      height: 5,
      content: "SLMC Reg {{slmcNumber}}",
      fontSize: 9,
      fontWeight: 400,
      align: "left",
      color: "#334155",
      fieldKey: "slmcNumber",
    }),
    text({
      id: "certificateNumber",
      label: "Certificate number",
      section: "body",
      x: 24,
      y: 104,
      width: 120,
      height: 5,
      content: "Certificate No: {{certificateNumber}}",
      fontSize: 9,
      fontWeight: 500,
      align: "left",
      color: "#334155",
      fieldKey: "certificateNumber",
      visible: false,
    }),
    text({
      id: "certificateDate",
      label: "Certificate date",
      section: "body",
      x: 173,
      y: 104,
      width: 100,
      height: 5,
      content: "Date: {{certificateDate}}",
      fontSize: 9,
      fontWeight: 500,
      align: "right",
      color: "#334155",
      fieldKey: "certificateDate",
      visible: false,
    }),
    text({
      id: "intro",
      label: "Certification intro",
      section: "body",
      ...full,
      y: 112,
      height: 22,
      content: body.certifyLine,
      fontSize: 12,
      fontWeight: 400,
      align: "left",
      lineHeight: 1.55,
      color: "#1e293b",
    }),
    text({
      id: "patientName",
      label: "Patient name",
      section: "body",
      ...full,
      y: 112,
      height: 8,
      content: "{{patientName}}",
      fontSize: 14,
      fontWeight: 700,
      align: "center",
      fieldKey: "patientName",
      visible: false,
    }),
    text({
      id: "patientDetails",
      label: "Patient details",
      section: "body",
      ...full,
      y: 120,
      height: 5,
      content: "Patient ID: {{patientId}}  ·  Age: {{patientAge}}  ·  {{patientGender}}",
      fontSize: 9,
      fontWeight: 400,
      align: "center",
      color: "#64748b",
      fieldKey: "patientId",
      visible: false,
    }),
    text({
      id: "treatmentType",
      label: "Treatment type",
      section: "body",
      ...full,
      y: 126,
      height: 6,
      content: "{{treatmentType}}",
      fontSize: 11,
      fontWeight: 400,
      align: "left",
      fieldKey: "treatmentType",
      visible: false,
    }),
    text({
      id: "treatmentDescription",
      label: "Treatment description",
      section: "body",
      ...full,
      y: 132,
      height: 6,
      content: "{{treatmentDescription}}",
      fontSize: 11,
      fontWeight: 400,
      align: "left",
      fieldKey: "treatmentDescription",
      visible: false,
    }),
    text({
      id: "reason",
      label: "Reason",
      section: "body",
      ...full,
      y: 138,
      height: 6,
      content: "Reason: {{reasonForLeave}}",
      fontSize: 11,
      fontWeight: 400,
      align: "left",
      fieldKey: "reasonForLeave",
      visible: false,
    }),
    text({
      id: "leaveDates",
      label: "Leave dates",
      section: "body",
      ...full,
      y: 144,
      height: 8,
      content: "Leave from {{leaveStartDate}} to {{leaveEndDate}} ({{leaveDays}} days)",
      fontSize: 11,
      fontWeight: 400,
      align: "left",
      fieldKey: "leaveStartDate",
      visible: false,
    }),
    text({
      id: "recommendationText",
      label: "Recommendation text",
      section: "body",
      ...full,
      y: 152,
      height: 8,
      content: "{{recommendationText}}",
      fontSize: 10,
      fontWeight: 400,
      align: "left",
      color: "#475569",
      fieldKey: "recommendationText",
      visible: false,
    }),
    text({
      id: "thankYou",
      label: "Footer text",
      section: "footer",
      ...full,
      y: 148,
      height: 8,
      content: "Thank You!",
      fontSize: 12,
      fontWeight: 400,
      align: "center",
      color: "#0f172a",
    }),
    text({
      id: "signature",
      label: "Doctor signature",
      section: "footer",
      x: 175,
      y: 168,
      width: 98,
      height: 16,
      content: "____________________\n{{doctorName}}",
      fontSize: 11,
      fontWeight: 700,
      align: "right",
      lineHeight: 1.5,
      color: "#0f172a",
    }),
  ];
}

function cloneTemplate(
  id: string,
  name: string,
  body: { title: string; certifyLine: string }
): CertificateTemplate {
  return {
    id,
    name,
    page: { ...PAGE },
    elements: standardElements(body),
  };
}

const CERTIFY =
  "I hereby certify that Mrs/Miss. {{patientName}} is having application for leave for {{treatmentDescription}}. I recommend {{leaveDays}} days leave from {{leaveStartDate}} to {{leaveEndDate}}";

export const DEFAULT_CERTIFICATE_TEMPLATES: CertificateTemplate[] = [
  cloneTemplate("standard-dental", "Standard Dental Certificate", {
    title: "Medical Certificate",
    certifyLine: CERTIFY,
  }),
  cloneTemplate("standard-medical", "Standard Medical Certificate", {
    title: "Medical Certificate",
    certifyLine: CERTIFY,
  }),
  cloneTemplate("orthodontic", "Orthodontic Certificate", {
    title: "Orthodontic Certificate",
    certifyLine:
      "I hereby certify that Mrs/Miss. {{patientName}} is having application for leave for {{treatmentType}}. I recommend {{leaveDays}} days leave from {{leaveStartDate}} to {{leaveEndDate}}",
  }),
  cloneTemplate("dental-surgery", "Dental Surgery Certificate", {
    title: "Dental Surgery Certificate",
    certifyLine:
      "I hereby certify that Mrs/Miss. {{patientName}} has undergone / is scheduled for {{treatmentType}}. I recommend {{leaveDays}} days leave from {{leaveStartDate}} to {{leaveEndDate}}",
  }),
  cloneTemplate("leave", "Leave Certificate", {
    title: "Leave Certificate",
    certifyLine:
      "I hereby certify that Mrs/Miss. {{patientName}} requires leave in relation to {{treatmentType}}. Leave is certified from {{leaveStartDate}} to {{leaveEndDate}} ({{leaveDays}} days).",
  }),
];

export const DEFAULT_TEMPLATE_ID = DEFAULT_CERTIFICATE_TEMPLATES[0].id;

export function cloneCertificateTemplate(
  template: CertificateTemplate,
  next: { id: string; name: string }
): CertificateTemplate {
  return {
    id: next.id,
    name: next.name,
    page: { ...template.page },
    elements: template.elements.map((el) => ({ ...el })),
  };
}

export function getDefaultTemplateById(id: string): CertificateTemplate | undefined {
  const found = DEFAULT_CERTIFICATE_TEMPLATES.find((item) => item.id === id);
  return found
    ? cloneCertificateTemplate(found, { id: found.id, name: found.name })
    : undefined;
}
